import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Folder,
  Download,
  Trash2,
  MoreVertical,
  Settings,
  FolderTree,
  Upload,
  RefreshCw,
  Edit3,
  MapPin,
} from "lucide-react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import changeStatusIcon from "../../../assets/icons/changeStatusIcon.png";
import { ITEM_TYPE } from "../constants/repoConstants";
import { canModifyRepoItem } from "../utils/canModifyRepoItem";
import { formatBytes } from "../utils/formatBytes";
import { getItemTypeDisplay } from "../utils/getItemTypeDisplay";
import { getRepoItemActionState } from "../utils/repoActionUtils";
import { getAvailableActions } from "../utils/getAvailableActions";

import { ReactComponent as ChatIcon } from "../../../assets/UserIcons/NewChatIcon2.svg";

export const DraggableItem = ({
  item,
  viewMode,
  onNavigate,
  onToggleSelect,
  onAction,
  isSelected,
  getFileIcon,
  onMove,
  role,
  repoView,
  onApprovalStatusChange,
  dragSelectionRef,
  selectedDragItems,
  updatingItems = new Set(),
  openActionMenuItemId,
  onToggleActionMenu,
  onOpenActionMenu,
  onCloseActionMenu,
  onChatItem,
  showLocation = false,
  locationPath = "",
  disableContextMenu = false,
  hideActions = false,
  disableSelection = false,
  showDocStatusColumn = true,
  showStatusColumn = true,
  allItems = [],
}) => {
  const clickTimerRef = useRef(null);
  const isSelectionDisabled = disableSelection;

  // Check if user can move/modify this item
  // In the refined system, admins can modify everything. Regular users can modify personal items only.
  const canModify = canModifyRepoItem(item, role);

  // In Group Repository view, only admins can drag-and-drop
  const isGroupView = repoView === "group";
  const canDragInGroupView = isGroupView
    ? role === "admin" && !item.isGroupRoot
    : true;
  const effectiveCanModify = isGroupView ? canDragInGroupView : canModify;

  const dragItems =
    isSelected && selectedDragItems.length > 0 ? selectedDragItems : [item];

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  const {
    canDownload,
    canChat,
    canChangeStatus,
    canRenameOrMove,
    canDelete,
    canRestore,
  } = getRepoItemActionState(item, role, repoView, 1, allItems);

  const availableActions = getAvailableActions({
    item,
    role,
    repoView,
    ui: "menu",
    selectionCount: 1,
    items: allItems,
  });

  const hasAction = (action) => availableActions.includes(action);

  // const isGroupView = repoView === "group";
  const isGroupUser = isGroupView && role !== "admin";
  const isGroupAdmin = isGroupView && role === "admin";

  const hasActions =
    !item.isGroupRoot && !hideActions && availableActions.length > 0;

  const actionButtonRef = useRef(null);
  const actionMenuRef = useRef(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState({
    type: "button",
    x: 0,
    y: 0,
  });
  const [actionMenuStyle, setActionMenuStyle] = useState({
    position: "absolute",
    minWidth: "160px",
    maxWidth: "240px",
    left: 0,
    top: "100%",
    bottom: "auto",
    right: "auto",
    transform: "none",
    visibility: "hidden",
    zIndex: 1200,
  });

  const calculateActionMenuPosition = () => {
    if (!actionMenuRef.current) {
      setActionMenuStyle((prev) => ({ ...prev, visibility: "hidden" }));
      return;
    }

    const menuRect = actionMenuRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const style = {
      minWidth: "160px",
      maxWidth: "240px",
      left: 0,
      top: "100%",
      bottom: "auto",
      right: "auto",
      transform: "none",
      visibility: "visible",
      zIndex: 1200,
    };

    if (actionMenuAnchor.type === "context") {
      let left = actionMenuAnchor.x;
      let top = actionMenuAnchor.y;

      if (left + menuRect.width > viewportWidth) {
        left = Math.max(8, viewportWidth - menuRect.width - 8);
      }

      if (top + menuRect.height > viewportHeight) {
        top = Math.max(8, viewportHeight - menuRect.height - 8);
      }

      setActionMenuStyle({
        ...style,
        position: "fixed",
        left,
        top,
      });
      return;
    }

    if (!actionButtonRef.current) {
      setActionMenuStyle((prev) => ({ ...prev, visibility: "hidden" }));
      return;
    }

    const buttonRect = actionButtonRef.current.getBoundingClientRect();
    const bottomSpace = viewportHeight - buttonRect.bottom;
    const topSpace = buttonRect.top;
    const rightSpace = viewportWidth - buttonRect.left;
    const leftSpace = buttonRect.right;

    style.position = "absolute";

    if (bottomSpace < menuRect.height && topSpace > bottomSpace) {
      style.top = "auto";
      style.bottom = "100%";
    }

    if (rightSpace < menuRect.width && leftSpace > menuRect.width) {
      style.left = "auto";
      style.right = 0;
    }

    setActionMenuStyle(style);
  };

  useLayoutEffect(() => {
    if (openActionMenuItemId === item.id) {
      calculateActionMenuPosition();
    } else {
      setActionMenuStyle((prev) => ({
        ...prev,
        left: 0,
        top: "100%",
        bottom: "auto",
        right: "auto",
        visibility: "hidden",
      }));
    }
  }, [actionMenuAnchor, item.id, openActionMenuItemId]);

  useEffect(() => {
    if (openActionMenuItemId !== item.id) {
      return undefined;
    }

    const handleOutsideActionMenuClick = (event) => {
      const target = event.target;

      if (
        actionMenuRef.current?.contains(target) ||
        actionButtonRef.current?.contains(target)
      ) {
        return;
      }

      onCloseActionMenu && onCloseActionMenu();
    };

    document.addEventListener("mousedown", handleOutsideActionMenuClick);
    document.addEventListener("touchstart", handleOutsideActionMenuClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideActionMenuClick);
      document.removeEventListener("touchstart", handleOutsideActionMenuClick);
    };
  }, [item.id, onCloseActionMenu, openActionMenuItemId]);

  const handleOpenActionMenuFromButton = (event) => {
    if (!hasActions) return;
    event.stopPropagation();
    setActionMenuAnchor({ type: "button", x: 0, y: 0 });
    onToggleActionMenu && onToggleActionMenu(item.id);
  };

  const handleOpenActionMenuFromContext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setActionMenuAnchor({
      type: "context",
      x: event.clientX,
      y: event.clientY,
    });
    onOpenActionMenu && onOpenActionMenu(item.id);
  };

  const handleMenuAction = (event, action) => {
    event.stopPropagation();
    onCloseActionMenu && onCloseActionMenu();
    if (action === "chat") {
      onChatItem?.(item);
      return;
    }
    onAction(action, item);
  };

  const handleStatusMenuAction = (event) => {
    event.stopPropagation();
    onCloseActionMenu && onCloseActionMenu();

    if (item.isPersonalSpace) {
      if (item.fileStatus === "1") {
        onAction("process", item);
      } else {
        onAction("revert", item);
      }
      return;
    }

    onAction("status", item);
  };

  const renderActionMenu = () => {
    // Group Repository — group root folders have no actions
    if (item.isGroupRoot || hideActions) return null;

    return (
      <ul
        ref={actionMenuRef}
        className={`dropdown-menu repo-action-menu ${openActionMenuItemId === item.id ? " show" : ""}`}
        style={actionMenuStyle}
      >
        {hasAction("chat") && (
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              style={{ maxWidth: "100%" }}
              onClick={(e) => handleMenuAction(e, "chat")}
            >
              <ChatIcon
                className="icon-image me-2"
                style={{ width: 16, height: 16 }}
              />
              Chat
            </button>
          </li>
        )}

        {/* Download — users and admins */}
        {hasAction("download") && (
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              style={{ maxWidth: "100%" }}
              onClick={(e) => handleMenuAction(e, "download")}
            >
              <Download size={16} /> Download
            </button>
          </li>
        )}

        {hasAction("rename") && (
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              style={{ maxWidth: "100%" }}
              onClick={(e) => handleMenuAction(e, "rename")}
            >
              <Edit3 size={16} /> Rename
            </button>
          </li>
        )}

        {hasAction("move") && (
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              style={{ maxWidth: "100%" }}
              onClick={(e) => handleMenuAction(e, "move")}
            >
              <FolderTree size={16} /> Move
            </button>
          </li>
        )}

        {/* Change Status — group admin only for files */}
        {(hasAction("changeStatus") ||
          hasAction("trainFile") ||
          hasAction("restoreFile")) && (
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              style={{ maxWidth: "100%" }}
              onClick={handleStatusMenuAction}
            >
              {item.isPersonalSpace ? (
                item.fileStatus === "1" ? (
                  <>
                    <Upload size={16} className="" /> Train File
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} className="" /> Restore File
                  </>
                )
              ) : (
                <>
                  <RefreshCw size={16} className="" /> Change Status
                </>
              )}
            </button>
          </li>
        )}

        {hasAction("delete") && (
          <li>
            <button
              className="dropdown-item text-danger d-flex align-items-center"
              style={{ maxWidth: "100%" }}
              onClick={(e) => handleMenuAction(e, "delete")}
            >
              <Trash2 size={16} /> Delete
            </button>
          </li>
        )}
      </ul>
    );
  };

  const shouldRenderContextMenu =
    openActionMenuItemId === item.id && actionMenuAnchor.type === "context";
  const shouldRenderButtonMenu =
    openActionMenuItemId === item.id && actionMenuAnchor.type === "button";

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ITEM_TYPE,
      item: () => ({
        id: item.id,
        type: item.type,
        name: item.name,
        groupId: item.groupId,
        items: dragItems,
        count: dragItems.length,
      }),
      // In group view: only admin can drag, and group root folders are immovable
      canDrag: isGroupView ? role === "admin" && !item.isGroupRoot : canModify,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [item, dragItems, canModify, isGroupView, role],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const getDraggedItems = (draggedItem) => draggedItem.items || [draggedItem];

  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    canDrop: (draggedItem) => {
      const draggedItems = getDraggedItems(draggedItem);
      const draggedIds = new Set(draggedItems.map((dragItem) => dragItem.id));

      if (item.type !== "folder" || draggedIds.has(item.id)) return false;

      // Group root folders cannot receive drops (no cross-group moves)
      if (item.isGroupRoot) return false;

      // In group view: all dragged items must share the same groupId as the target
      if (isGroupView) {
        const targetGroupId = item.groupId;
        const hasCrossGroup = draggedItems.some(
          (di) =>
            di.groupId &&
            targetGroupId &&
            String(di.groupId) !== String(targetGroupId),
        );
        if (hasCrossGroup) return false;
        return role === "admin";
      }

      return canModify;
    },
    drop: (draggedItem) => onMove(getDraggedItems(draggedItem), item.id),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const combinedRef = (node) => {
    drag(node);
    if (item.type === "folder") drop(node);
  };

  const renderLocationText = (className = "repo-item-location") => {
    if (!showLocation || !locationPath) return null;

    return (
      <span className={className} title={locationPath}>
        {locationPath}
      </span>
    );
  };

  if (viewMode === "grid") {
    return (
      <>
        <div
          ref={combinedRef}
          data-item-id={String(item.id)}
          className={`repo-card ${isSelected ? "selected" : ""} ${isOver ? "drop-target" : ""} ${!canModify ? "read-only" : ""}`}
          style={{ opacity: isDragging ? 0.5 : 1 }}
          onContextMenu={
            disableContextMenu || !hasActions
              ? undefined
              : handleOpenActionMenuFromContext
          }
          onClick={(e) => {
            if (dragSelectionRef.current) {
              return;
            }
            // For folders, debounce to wait for potential double-click
            if (item.type === "folder") {
              if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
              clickTimerRef.current = setTimeout(() => {
                onToggleSelect(item.id);
                clickTimerRef.current = null;
              }, 300);
            } else {
              onToggleSelect(item.id);
            }
          }}
          onDoubleClick={(e) => {
            if (item.type === "folder") {
              // Clear pending single-click selection
              if (clickTimerRef.current) {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = null;
              }
              onNavigate(item.id, item.name);
            }
          }}
        >
          <div
            className="repo-card-checkbox"
            onClick={(e) => {
              if (disableSelection) {
                return;
              }
              e.stopPropagation();
              onToggleSelect(item.id);
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              readOnly
              disabled={disableSelection}
              aria-disabled={disableSelection}
              style={{
                cursor: disableSelection ? "not-allowed" : "pointer",
                opacity: disableSelection ? 0.45 : 1,
              }}
            />
          </div>

          <div className="repo-card-icon mb-1">
            {item.type === "folder" ? (
              <Folder
                className="repo-folder-icon"
                size={48}
                fill="currentColor"
                fillOpacity={0.2}
              />
            ) : (
              getFileIcon(item, 48)
            )}
          </div>

          <div className="repo-card-content d-flex flex-column gap-1">
            <div className="repo-card-name" title={item.name}>
              {item.name?.charAt(0).toUpperCase() + item.name?.slice(1)}
            </div>
            {showLocation && locationPath && (
              <div className="repo-item-location--grid" title={locationPath}>
                <MapPin
                  size={12}
                  className="repo-item-location-icon"
                  aria-hidden
                />
                <span className="repo-item-location">{locationPath}</span>
              </div>
            )}
            <div className="repo-card-details">
              <span className="repo-card-type">{getItemTypeDisplay(item)}</span>
              {item.type === "file" && item.size && (
                <span className="repo-card-size">
                  • {formatBytes(item.size)}
                </span>
              )}
            </div>
            <div className="repo-card-date">
              {new Date(item.updatedAt).toLocaleDateString()}
            </div>
          </div>

          <div className="repo-card-footer">
            {item.type === "file" && (
              <span
                className={`badge  ${item.fileStatus === "1" ? "bg-success" : ""}`}
              >
                {item.fileStatus === "1" ? "Active" : "Inactive"}
              </span>
            )}
            {item.mode === "global" && (
              <span
                className="badge "
                style={{ color: "var(--repo-text-main)" }}
              >
                Global
              </span>
            )}
          </div>

          {!hideActions && (
            <div className="repo-card-actions">
              <div
                className="dropdown border-0"
                style={{ position: "relative" }}
              >
                <button
                  ref={actionButtonRef}
                  className="repo-action-dot bg-transparent border-0"
                  type="button"
                  onClick={handleOpenActionMenuFromButton}
                  disabled={!hasActions}
                  title={!hasActions ? "No actions available" : "Actions"}
                  style={{
                    opacity: !hasActions ? 0.5 : 1,
                    cursor: !hasActions ? "not-allowed" : "pointer",
                  }}
                >
                  <MoreVertical size={16} className="icon-image" />
                </button>
                {shouldRenderButtonMenu && hasActions && renderActionMenu()}
              </div>
            </div>
          )}
        </div>
        {shouldRenderContextMenu &&
          hasActions &&
          createPortal(renderActionMenu(), document.body)}
      </>
    );
  }

  return (
    <>
      <tr
        ref={combinedRef}
        data-item-id={String(item.id)}
        className={`repo-list-row ${isSelected ? "selected" : ""} ${isOver ? "drop-target" : ""} ${!canModify ? "read-only" : ""}`}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        onContextMenu={
          disableContextMenu || !hasActions
            ? undefined
            : handleOpenActionMenuFromContext
        }
      >
        <td style={{ width: "40px" }} onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            disabled={disableSelection}
            aria-disabled={disableSelection}
            onChange={() => {
              if (!disableSelection) {
                onToggleSelect(item.id);
              }
            }}
            style={{
              cursor: disableSelection ? "not-allowed" : "pointer",
              opacity: disableSelection ? 0.45 : 1,
            }}
          />
        </td>
        <td
          onClick={(e) => {
            if (dragSelectionRef.current) {
              return;
            }
            // For folders, debounce to wait for potential double-click
            if (item.type === "folder") {
              if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
              clickTimerRef.current = setTimeout(() => {
                onToggleSelect(item.id);
                clickTimerRef.current = null;
              }, 300);
            } else {
              onToggleSelect(item.id);
            }
          }}
          onDoubleClick={(e) => {
            if (item.type === "folder") {
              // Clear pending single-click selection
              if (clickTimerRef.current) {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = null;
              }
              onNavigate(item.id, item.name);
            }
          }}
          style={{ cursor: "pointer" }}
        >
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            {item.type === "folder" ? (
              <Folder size={18} className="repo-folder-icon" />
            ) : (
              getFileIcon(item, 18)
            )}
            <span
              style={{
                maxWidth: "250px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={item.name}
            >
              {item.name?.charAt(0).toUpperCase() + item.name?.slice(1)}
              {/* {item.name} */}
            </span>
            {/* {item.mode === "global" && (
            <span className="badge bg-info ms-2" >
              Global
            </span>
          )} */}
          </div>
        </td>
        {showLocation && (
          <td className="repo-location-cell">{renderLocationText()}</td>
        )}
        <td>{getItemTypeDisplay(item)}</td>
        <td>
          {item.type === "file"
            ? item.size
              ? formatBytes(item.size)
              : "0 Bytes"
            : ""}
        </td>
        {!item.isPersonalSpace && showDocStatusColumn && (
          <td>
            {item.type === "file" ? (
              role === "admin" ? (
                <select
                  className={`badge repo-approval-badge ${
                    item.approvalStatus === "approved"
                      ? "bg-success"
                      : item.approvalStatus === "duplicate"
                        ? "bg-duplicate"
                        : item.approvalStatus === "pending"
                          ? "bg-warning"
                          : "bg-danger"
                  }`}
                  value={item.approvalStatus || "pending"}
                  onChange={(e) => {
                    e.stopPropagation();
                    onApprovalStatusChange &&
                      onApprovalStatusChange(item.id, e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    cursor: "pointer",
                    outline: "none",
                    opacity: updatingItems.has(item.id) ? 0.5 : 1,
                  }}
                  disabled={
                    updatingItems.has(item.id) ||
                    item.approvalStatus === "duplicate"
                  }
                >
                  <option value={item.approvalStatus || "pending"} hidden>
                    {(item.approvalStatus || "pending")
                      .charAt(0)
                      .toUpperCase() +
                      (item.approvalStatus || "pending").slice(1)}
                  </option>

                  {/* {item.approvalStatus !== "pending" && (
                    <option value="pending">Pending</option>
                  )} */}

                  {item.approvalStatus !== "approved" && (
                    <option value="approved">Approved</option>
                  )}

                  {item.approvalStatus !== "rejected" && (
                    <option value="rejected">Rejected</option>
                  )}

                  {/* {item.approvalStatus !== "duplicate" && (
                    <option value="duplicate">Duplicate</option>
                  )} */}
                </select>
              ) : (
                <span
                  className={`badge repo-approval-badge ${
                    item.approvalStatus === "approved"
                      ? "bg-success"
                      : item.approvalStatus === "duplicate"
                        ? "bg-duplicate"
                        : item.approvalStatus === "pending"
                          ? "bg-warning"
                          : "bg-danger"
                  }`}
                >
                  {(item.approvalStatus || "Pending").charAt(0).toUpperCase() +
                    (item.approvalStatus || "Pending").slice(1)}
                </span>
              )
            ) : (
              ""
            )}
          </td>
        )}
        {!item.isPersonalSpace && showStatusColumn && (
          <td>
            {item.type === "file" && (
              <span
                className={`badge repo-status-badge ${
                  item.fileStatus === "1" ? "bg-active" : "bg-inactive"
                }`}
              >
                {item.fileStatus === "1" ? "Active" : "Inactive"}
              </span>
            )}
          </td>
        )}
        {/* <td>
        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}
      </td> */}
        <td>
          {item.updatedAt
            ? new Date(item.updatedAt).toLocaleDateString()
            : "N/A"}
        </td>
        {role === "admin" && <td>{item.uploadedBy || "N/A"}</td>}
        {!hideActions && (
          <td className="text-center align-middle action-cell">
            <div
              className="dropdown border-0  d-flex align-items-center justify-content-center"
              style={{ position: "relative" }}
            >
              <button
                ref={actionButtonRef}
                className="repo-action-dot bg-transparent border-0"
                type="button"
                onClick={handleOpenActionMenuFromButton}
                disabled={!hasActions}
                title={!hasActions ? "No actions available" : "Actions"}
                style={{
                  opacity: !hasActions ? 0.5 : 1,
                  cursor: !hasActions ? "not-allowed" : "pointer",
                }}
              >
                <MoreVertical size={16} />
              </button>
              {shouldRenderButtonMenu && hasActions && renderActionMenu()}
            </div>
          </td>
        )}
      </tr>
      {shouldRenderContextMenu &&
        hasActions &&
        createPortal(renderActionMenu(), document.body)}
    </>
  );
};
